import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Row,
    Col,
    Typography,
    Button,
    Select,
    Space,
    message,
    Modal,
    Skeleton,
    Divider,
    Table,
    Alert,
    Popconfirm,
} from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

import axiosInstance from '../../../configs';
import InvoiceHeader from './Shared/InvoiceHeader';
import CustomerSection from './Shared/CustomerSection';
import DateFields from './Shared/DateFields';
import TotalSummary from './Shared/TotalSummary';
import ServiceTable from './Shared/ServiceTable';

const { Title, Text } = Typography;

export default function StorageEdit() {
    const history = useHistory();
    const { id } = useParams();

    const [, setLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(true);
    const [invoiceData, setInvoiceData] = useState(null);

    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [statusGrup, setStatusGrup] = useState('');

    const [calculatedPeriods, setCalculatedPeriods] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [storageTiers, setStorageTiers] = useState([]);

    // DETIL ITEM state
    const [nopenItems, setNopenItems] = useState([]);
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [selectedItemCode, setSelectedItemCode] = useState('');

    useEffect(() => {
        fetchReferences();
        fetchTiers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTiers = async () => {
        try {
            const res = await axiosInstance.get('/referensi-finance/storage-tiers');
            setStorageTiers(res.data.data || []);
        } catch (err) {
            console.error('❌ Failed to fetch tiers:', err);
        }
    };

    const fetchReferences = async () => {
        setInitLoading(true);
        try {

            // Robust fetch
            let contactsData = [];
            try {
                const resContacts = await axiosInstance.get('/finance/contacts');
                contactsData = resContacts.data.data || [];
            } catch (e) {
                console.error('❌ Contacts fetch failed', e);
            }
            setContacts(contactsData);

            if (id) {

                try {
                    const resInv = await axiosInstance.get(`/finance/sales-invoices/${id}`);
                    const inv = resInv.data.data;
                    if (inv) {
                        setInvoiceData(inv);
                        if (inv.invoice_items) setInvoiceItems(inv.invoice_items);
                        if (inv.nopen) {
                            try {
                                const resItems = await axiosInstance.get(`/finance/nopen-items/${inv.nopen}`);
                                setNopenItems(resItems.data.data || []);
                            } catch (e) { /* empty */ }
                        }
                    }
                } catch (e) {
                    console.error('❌ Invoice fetch failed', e);
                }
            }
        } catch (err) {
            console.error('❌ Error in fetchReferences:', err);
        } finally {
            setInitLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (!invoiceData) return {
            invoiceNumber: '',
            invoiceDate: dayjs(),
            dueDate: dayjs(),
            periodStart: dayjs(),
            periodEnd: dayjs(),
            documentNumber: '',
            documentDate: dayjs(),
        };
        return {
            invoiceNumber: invoiceData.invoice_number,
            invoiceDate: invoiceData.invoice_date ? dayjs(invoiceData.invoice_date) : dayjs(),
            dueDate: invoiceData.due_date ? dayjs(invoiceData.due_date) : dayjs(),
            periodStart: invoiceData.period_start ? dayjs(invoiceData.period_start) : dayjs(invoiceData.document_date),
            periodEnd: invoiceData.period_end ? dayjs(invoiceData.period_end) : dayjs(),
            documentNumber: invoiceData.nopen || '',
            documentDate: invoiceData.document_date ? dayjs(invoiceData.document_date) : null,
        };
    }, [invoiceData]);

    const [values, setValues] = useState(initialValues);

    useEffect(() => {
        setValues(initialValues);
    }, [initialValues]);

    const setFieldValue = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {


        if (invoiceData && contacts.length > 0) {
            const matchedContact = contacts.find(c => String(c.idContact) === String(invoiceData.id_customer));

            if (matchedContact) {
                setSelectedContact(matchedContact);
                setStatusGrup(matchedContact.contactType === 0 ? 'Grup' : 'Non Grup'); // 0 is Grup
            }

            // Populate calculatedPeriods from saved finance_invoice_details
            if (invoiceData.items && invoiceData.items.length > 0 && calculatedPeriods.length === 0) {
                const savedItems = invoiceData.items.map((d, idx) => ({
                    id: d.id_detail || `saved-${idx}`,
                    name: d.item_name || '',
                    description: '',
                    qty: Number(d.qty || 0),
                    price: Number(d.unit_price || 0),
                    amountBeforeTax: Number(d.sub_amount || 0),
                    taxRate: Number(d.tax_rate || 0),
                    taxAmount: Number(d.tax_amount || 0),
                    total: Number(d.total_amount || 0),
                }));
                setCalculatedPeriods(savedItems);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceData, contacts]);

    // Calculate storage periods automatically when period changes
    useEffect(() => {
        if (!invoiceData || !initialValues.periodStart || !initialValues.periodEnd || storageTiers.length === 0) return;

        // Skip auto-calculation if invoice already has items from DB
        if (invoiceData.items && invoiceData.items.length > 0) return;

        calculateStoragePeriods(initialValues.periodStart, initialValues.periodEnd);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceData, initialValues.periodStart, initialValues.periodEnd, storageTiers]);

    const calculateStoragePeriods = (startDate, endDate) => {
        const periods = [];
        const QTY = invoiceData?.qty || 1;

        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const totalDays = end.diff(start, 'day') + 1;

        let periodIdx = 1;

        storageTiers.forEach(tier => {
            // Find intersection between invoice duration [1, totalDays] and tier [min, max]
            const effectiveStart = Math.max(1, tier.min_day);
            const effectiveEnd = Math.min(totalDays, tier.max_day);

            if (effectiveStart <= effectiveEnd) {
                const daysInTier = (effectiveEnd - effectiveStart) + 1;
                const tierPrice = tier.is_free ? 0 : tier.rate_per_day;
                const amountBeforeTax = QTY * tierPrice * daysInTier;
                const taxRate = 11;
                const taxAmount = amountBeforeTax * (taxRate / 100);
                const amount = amountBeforeTax + taxAmount;

                if (daysInTier > 0) {
                    const pStartStr = start.add(effectiveStart - 1, 'day').format('YYYY-MM-DD');
                    const pEndStr = start.add(effectiveEnd - 1, 'day').format('YYYY-MM-DD');

                    periods.push({
                        key: tier.id_tier,
                        name: tier.name,
                        description: `${pStartStr} - ${pEndStr} (${daysInTier} hari)`,
                        qty: QTY,
                        price: tierPrice,
                        amountBeforeTax: amountBeforeTax,
                        taxRate: taxRate,
                        taxAmount: taxAmount,
                        total: amount
                    });
                }
            }
        });

        // Fallback if no tiers match
        if (periods.length === 0) {
            periods.push({
                key: 0,
                name: 'Penyimpanan',
                description: `${start.format('YYYY-MM-DD')} - ${end.format('YYYY-MM-DD')}`,
                qty: QTY,
                price: 0,
                amountBeforeTax: 0,
                taxRate: 11,
                taxAmount: 0,
                total: 0
            });
        }

        setCalculatedPeriods(periods);
    };

    const totalAll = calculatedPeriods.reduce((acc, curr) => acc + curr.total, 0);
    const totalPajak = calculatedPeriods.reduce((acc, curr) => acc + (curr.taxAmount || 0), 0);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                // Map calculatedPeriods to items for backend compatibility
                items: calculatedPeriods.map(p => ({
                    item_name: p.description ? `${p.name} (${p.description})` : p.name,
                    qty: p.qty,
                    unit_price: p.price,
                    sub_amount: p.amountBeforeTax || 0,
                    tax_rate: p.taxRate || 0,
                    tax_amount: p.taxAmount || 0,
                    total_amount: p.total
                })),
                totalAmount: totalAll,
                invoiceDate: values.invoiceDate.format('YYYY-MM-DD'),
                dueDate: values.dueDate.format('YYYY-MM-DD'),
                periodStart: values.periodStart ? values.periodStart.format('YYYY-MM-DD') : null,
                periodEnd: values.periodEnd ? values.periodEnd.format('YYYY-MM-DD') : null,
            };

            if (id) {
                await axiosInstance.put(`/finance/sales-invoices/${id}`, payload);
                message.success('Invoice berhasil disimpan!');
                setTimeout(() => history.push('/finance'), 1500);
            }
        } catch (err) {
            console.error(err);
            message.error('Gagal menyimpan invoice');
        } finally {
            setLoading(false);
            setConfirmOpen(false);
        }
    };

    const getInvoiceTypeLabel = () => 'Storage';

    // DETIL ITEM handlers
    const availableItems = nopenItems.filter(ni =>
        !invoiceItems.some(ii => ii.item_code === ni.item_code && ii.lokasi_lot === ni.lokasi_lot)
    );

    const handleAddInvoiceItem = async () => {
        if (!selectedItemCode || !id) return;
        const item = nopenItems.find(i => `${i.item_code}|${i.lokasi_lot}` === selectedItemCode);
        if (!item) return;
        try {
            const res = await axiosInstance.post('/finance/invoice-items', {
                id_invoice: Number(id), item_code: item.item_code, item_name: item.item_name,
                jumlah_kemasan: item.jumlah_kemasan, lokasi_lot: item.lokasi_lot,
            });
            setInvoiceItems(prev => [...prev, { id_invoice_item: res.data.data.id_invoice_item, ...item }]);
            setSelectedItemCode('');
            message.success('Item ditambahkan');
        } catch (err) { message.error('Gagal menambahkan item'); }
    };

    const handleDeleteInvoiceItem = async (itemId) => {
        try {
            await axiosInstance.delete(`/finance/invoice-items/${itemId}`);
            setInvoiceItems(prev => prev.filter(i => i.id_invoice_item !== itemId));
            message.success('Item dihapus');
        } catch (err) { message.error('Gagal menghapus item'); }
    };

    const handleQtyChange = (idx, val) => {
        const ni = [...calculatedPeriods];
        ni[idx].qty = val;
        ni[idx].amountBeforeTax = val * ni[idx].price;
        ni[idx].taxAmount = val * ni[idx].price * (ni[idx].taxRate / 100);
        ni[idx].total = ni[idx].amountBeforeTax + ni[idx].taxAmount;
        setCalculatedPeriods(ni);
    };

    const handlePriceChange = (idx, val) => {
        const ni = [...calculatedPeriods];
        ni[idx].price = val;
        ni[idx].amountBeforeTax = val * ni[idx].qty;
        ni[idx].taxAmount = ni[idx].amountBeforeTax * (ni[idx].taxRate / 100);
        ni[idx].total = ni[idx].amountBeforeTax + ni[idx].taxAmount;
        setCalculatedPeriods(ni);
    };

    const handleRemoveItem = (idx) => {
        const ni = [...calculatedPeriods];
        ni.splice(idx, 1);
        setCalculatedPeriods(ni);
    };

    return (
        <>
            <div style={{ marginBottom: 16 }}>
                <Title level={3}>Edit Invoice Storage</Title>
                <Text type="secondary">Finance / Edit / Storage</Text>
            </div>

            <Card>
                {initLoading ? <Skeleton active /> : (
                    <div>
                        <Row gutter={[16, 24]}>
                            <Col span={24}>
                                <InvoiceHeader
                                    invoiceData={invoiceData}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    getInvoiceTypeLabel={getInvoiceTypeLabel}
                                />
                            </Col>

                            <Col span={24}>
                                <CustomerSection
                                    selectedContact={selectedContact}
                                    statusGrup={statusGrup}
                                />
                            </Col>

                            <Col span={24}>
                                <Divider />
                            </Col>

                            {/* Storage Info - AUTO CALCULATE */}
                            <Col span={24}>
                                <Alert
                                    message="ℹ️ Informasi Biaya Storage"
                                    description={
                                        <div>
                                            <p>• Biaya storage dihitung otomatis berdasarkan durasi penyimpanan dan <strong>Tiering Storage</strong> yang diset di Master Data.</p>
                                            <p>• Rincian tarif per periode dapat dilihat pada tabel di bawah.</p>
                                            <p style={{ fontStyle: 'italic', marginTop: 8 }}>
                                                Ubah "Periode Penyimpanan (End)" untuk menyesuaikan durasi dan biaya
                                            </p>
                                        </div>
                                    }
                                    type="info"
                                    showIcon
                                />
                            </Col>

                            <Col span={24}>
                                <DateFields
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    showPeriod={true}
                                />
                            </Col>

                            <Col span={24}>
                                <Divider />
                            </Col>

                            {/* Calculated Breakdown */}
                            <Col span={24}>
                                <Title level={5} style={{ marginBottom: 16 }}>Storage</Title>
                                <ServiceTable
                                    firstColumnTitle="Nama Storage"
                                    lineItems={calculatedPeriods}
                                    handleQtyChange={handleQtyChange}
                                    handlePriceChange={handlePriceChange}
                                    handleRemoveItem={handleRemoveItem}
                                />
                            </Col>

                            <Col span={24}>
                                <Divider />
                            </Col>

                            {/* DETIL ITEM */}
                            <Col span={24}>
                                <Title level={5} style={{ marginBottom: 16 }}>DETIL ITEM</Title>
                                <Space style={{ width: '100%', marginBottom: 12 }} size="middle">
                                    <Select style={{ width: 500 }} value={selectedItemCode}
                                        onChange={(val) => setSelectedItemCode(val)}
                                        placeholder="Pilih item dari nopen..." showSearch optionFilterProp="children">
                                        {availableItems.map((item, idx) => (
                                            <Select.Option key={`${item.item_code}|${item.lokasi_lot}|${idx}`}
                                                value={`${item.item_code}|${item.lokasi_lot}`}>
                                                {item.item_code} — {item.item_name} (Lot: {item.lokasi_lot || '-'}, Qty: {item.jumlah_kemasan})
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <Button icon={<PlusOutlined />} onClick={handleAddInvoiceItem} disabled={!selectedItemCode}>Tambah Item</Button>
                                </Space>
                                <Table dataSource={invoiceItems}
                                    rowKey={(r) => r.id_invoice_item || `${r.item_code}-${r.lokasi_lot}`}
                                    pagination={false} size="small"
                                    columns={[
                                        { title: 'Kode Barang', dataIndex: 'item_code', width: 150 },
                                        { title: 'Nama Barang', dataIndex: 'item_name' },
                                        { title: 'Jumlah Kemasan', dataIndex: 'jumlah_kemasan', width: 140, align: 'center' },
                                        { title: 'Lokasi Lot', dataIndex: 'lokasi_lot', width: 120 },
                                        {
                                            title: '#', key: 'action', width: 60, align: 'center',
                                            render: (_, record) => (
                                                <Popconfirm title="Hapus item ini?" onConfirm={() => handleDeleteInvoiceItem(record.id_invoice_item)}>
                                                    <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            )
                                        },
                                    ]}
                                    locale={{ emptyText: 'Belum ada item. Pilih dari dropdown di atas.' }}
                                />
                            </Col>

                            <Col span={24}>
                                <Divider />
                            </Col>

                            <Col span={24}>
                                <TotalSummary 
                                    totalAll={totalAll} 
                                    totalPajak={totalPajak} 
                                    otherAmount={invoiceData?.other_amount} 
                                />
                            </Col>

                            <Col span={24} style={{ textAlign: 'right' }}>
                                <Space>
                                    <Button size="large" onClick={() => history.push('/finance')}>
                                        Batal
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={() => setConfirmOpen(true)}
                                    >
                                        Simpan
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <Modal
                            title="Konfirmasi Simpan"
                            open={confirmOpen}
                            onOk={handleSubmit}
                            onCancel={() => setConfirmOpen(false)}
                            okText="Ya, Simpan"
                            cancelText="Batal"
                        >
                            <p>Apakah anda yakin ingin menyimpan perubahan invoice storage ini?</p>
                            <p><strong>Total biaya: Rp {totalAll.toLocaleString()}</strong></p>
                        </Modal>
                    </div>
                )}
            </Card>
        </>
    );
}
